import { useEffect, useRef, useState } from "react";
import { captureSubmissionError, isNetworkError } from "../utils/sentryUtils";

export const DRAFT_SAVE_DEBOUNCE_MS = 2000;

// useDoableProgress loads and autosaves a client's draft answers for a doable
// via the /progress endpoints. Drafts are best-effort: a failed load renders
// the form without one, and a failed save waits for the next change, so
// neither ever blocks the form.
//
// Saving is debounced, and the first saveDraft call records a baseline
// instead of saving, so merely opening a form (or restoring a draft) never
// creates or rewrites one. Whatever is still pending is flushed when the page
// hides or the form unmounts.
//
// enabled must be false for sessions the progress endpoints reject (anyone
// but the client) and while a postal 2FA gate is still unpassed.
const useDoableProgress = (doableId, enabled) => {
  const [settled, setSettled] = useState(false);
  const [draft, setDraft] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const timerRef = useRef(null);
  const pendingBodyRef = useRef(null);
  const lastSavedBodyRef = useRef(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const url = `/api/dashboard/doable/${doableId}/progress`;

  useEffect(() => {
    if (!enabled || settled) return;

    const controller = new AbortController();
    fetch(url, { credentials: "same-origin", signal: controller.signal })
      .then(async (resp) => {
        if (resp.ok) {
          const data = await resp.json();
          setDraft(data.progress);
          setSavedAt(data.updated_at);
        }
        // A 404 means no draft. Any other failure also settles: the form
        // must render, just without a draft.
        setSettled(true);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        console.error("Failed to load saved progress", err);
        setSettled(true);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- url is derived from doableId, which is fixed for a mounted form
  }, [doableId, enabled, settled]);

  const send = (body, keepalive) => {
    lastSavedBodyRef.current = body;
    fetch(url, { method: "PUT", credentials: "same-origin", body, keepalive })
      .then((resp) => {
        if (resp.ok) {
          setSavedAt(new Date().toISOString());
        } else if (resp.status !== 409) {
          // The doable is complete, so it holds no more drafts. A save sent
          // just before the client submits lands after the submission closes
          // the doable, and cancelPendingSave cannot recall a request already
          // in flight. The submission supersedes the draft, so the server is
          // right to refuse it.
          //
          // Two server errors become a 409, but only ErrDoableClosed can
          // reach this endpoint. doable_progress_test.go pins that.
          captureSubmissionError("DoableProgress", resp.status, "");
        }
      })
      .catch((err) => {
        // Being offline is routine for an autosave (a closed laptop, a train
        // tunnel); only unexpected failures are worth reporting.
        if (!isNetworkError(err)) {
          captureSubmissionError("DoableProgress", 0, String(err));
        }
      });
  };

  const cancelPendingSave = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    pendingBodyRef.current = null;
  };

  const flush = (keepalive) => {
    const body = pendingBodyRef.current;
    cancelPendingSave();
    if (body != null) {
      send(body, keepalive);
    }
  };

  // The listener and unmount cleanup call through a ref so registration
  // happens once but always flushes with the current doable's URL.
  const flushRef = useRef(() => {});
  flushRef.current = () => flush(true);

  useEffect(() => {
    const onPageHide = () => flushRef.current();
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      flushRef.current();
    };
  }, []);

  const saveDraft = (payload) => {
    if (!enabledRef.current) return;

    const body = JSON.stringify(payload);

    if (lastSavedBodyRef.current == null) {
      lastSavedBodyRef.current = body;
      return;
    }

    if (body === lastSavedBodyRef.current) {
      // Changed and changed back: drop any queued save of the interim state.
      cancelPendingSave();
      return;
    }

    pendingBodyRef.current = body;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(false), DRAFT_SAVE_DEBOUNCE_MS);
  };

  return {
    loadingDraft: enabled && !settled,
    draft,
    savedAt,
    saveDraft,
    cancelPendingSave,
  };
};

export default useDoableProgress;
